// https://github.com/department-stockholm/aws-signature-v4
import CryptoJS from 'crypto-js';

const urlSearchParamsToObject = entries => {
	const result = {};
	for (const [key, value] of entries) {
		// each 'entry' is a [key, value] tupple
		result[key] = value;
	}
	return result;
};

const createCanonicalRequest = (method, pathname, query, headers, payload) => {
	return [
		method.toUpperCase(),
		createCanonicalURI(pathname),
		createCanonicalQueryString(query),
		createCanonicalHeaders(headers),
		createSignedHeaders(headers),
		createCanonicalPayload(payload)
	].join('\n');
};

const escapePath = pathname => {
	return pathname
		.split(/\//g)
		.map(v => encodeURIComponent(v))
		.join('/');
};

const createCanonicalURI = uri => {
	try {
		const urlObject = new URL(uri);
		return `${escapePath(urlObject.pathname)}`;
	} catch (e) {
		// ignore, uri likely not valid URL format, so get path below
	}

	return escapePath(uri);
};

const createCanonicalPayload = payload => {
	if (payload === 'UNSIGNED-PAYLOAD') {
		return payload;
	}
	return hash(payload || '');
};

const createCanonicalQueryString = params => {
	if (!params) {
		return '';
	}
	if (typeof params == 'string') {
		params = urlSearchParamsToObject(new URLSearchParams(params));
	}
	return Object.keys(params)
		.sort()
		.map(function (key) {
			const values = Array.isArray(params[key]) ? params[key] : [params[key]];
			return values
				.sort()
				.map(value => {
					return encodeURIComponent(key) + '=' + encodeURIComponent(value);
				})
				.join('&');
		})
		.join('&');
};

const createCanonicalHeaders = headers => {
	return Object.keys(headers)
		.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
		.map(name => {
			const values = Array.isArray(headers[name]) ? headers[name] : [headers[name]];
			return (
				name.toLowerCase().trim() +
				':' +
				values
					.map(value => {
						return value.replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
					})
					.join(',') +
				'\n'
			);
		})
		.join('');
};

const createSignedHeaders = headers => {
	return Object.keys(headers)
		.map(name => {
			return name.toLowerCase().trim();
		})
		.sort()
		.join(';');
};

const createCredentialScope = (time, region, service) => {
	return [toDate(time), region, service, 'aws4_request'].join('/');
};

const createStringToSign = (time, region, service, request) => {
	return ['AWS4-HMAC-SHA256', toTime(time), createCredentialScope(time, region, service), hash(request)].join('\n');
};

const createAuthorizationHeader = (key, scope, signedHeaders, signature) => {
	return ['AWS4-HMAC-SHA256 Credential=' + key + '/' + scope, 'SignedHeaders=' + signedHeaders, 'Signature=' + signature].join(',');
};

const createSignature = (secret, time, region, service, stringToSign) => {
	const kDate = hmac('AWS4' + secret, toDate(time)); // key-date
	const kRegion = hmac(kDate, region); // key-region
	const kService = hmac(kRegion, service); // key-service
	const kSigning = hmac(kService, 'aws4_request'); // key-signing

	return hmac(kSigning, stringToSign).toString();
};

const toTime = time => {
	return new Date(time).toISOString().replace(/[:-]|\.\d{3}/g, '');
};

const toDate = time => {
	return toTime(time).substring(0, 8);
};

const hmac = (key, string) => {
	return CryptoJS.HmacSHA256(string, key);
};

const hash = string => {
	return CryptoJS.SHA256(string).toString();
};

export default {
	toTime,
	toDate,
	createCanonicalRequest,
	createCanonicalQueryString,
	createCanonicalHeaders,
	createSignedHeaders,
	createCredentialScope,
	createStringToSign,
	createAuthorizationHeader,
	createSignature
};
